import axios from "axios";
import { restEndpoint, WS_ENDPOINT } from "@/helpers/endpoints";
import { Store } from "vuex";
import { DARStore } from "@/types/store";
import {AuthResponse, InitialResponse} from "@/types/types";
import { AuthMessage } from "@/types/websocketMessages/incomeMessages";
import { sendWebsocketMessage } from "@/store/plugins/websocket/sendOutgoingMessages";
import { handleIncomingMessages } from "@/store/plugins/websocket/handleIncomingMessages";
import { OutcomeWebsocketMessage } from "@/types/websocketMessages/outcomeMessages";
import { initHeap } from "@/helpers/heap-loader"

let token: string | null;
export let connection: WebSocket;
let interval: ReturnType<typeof setInterval>;

export const getStoredToken = () => {
  try {
    return sessionStorage.getItem("clientToken");
  } catch (e) {
    return null;
  }
};

const getToken = () =>
  new Promise<string>((resolve, reject) => {
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get("hash");
    if (token) {
      return resolve(token);
    }
    reject();
  });

export const getInitialData = (token: string) =>
  new Promise<InitialResponse>((resolve, reject) => {
    axios
      .get(restEndpoint("/dar/init"), {
        headers: { "dar-client-token": token }
      })
      .then(({ data }) => resolve(data as InitialResponse))
      .catch(e => reject(e));
  });

const getClientToken = async (token: string) => {
  const { data }: { data: AuthResponse } = await axios.get(
    restEndpoint("/dar/auth"),
    {
      headers: { "dar-auth-token": token }
    }
  );
  return data;
};

export const createConnection = async (
  store: Store<DARStore>,
  isFirstConnection = true
) => {
  let clientToken = getStoredToken();
  if (!clientToken) {
    try {
      const token = await getToken();
      clientToken = (await getClientToken(token)).clientToken;
    } catch (e) {
      console.error("UNABLE TO GET TOKENS.");
      return;
    }
    try {
      const refineURL = () => {
        const currURL = window.location.href; //get current address
        const afterDomain = currURL.substring(currURL.lastIndexOf("/") + 1);
        return afterDomain.split("?")[0];
      };
      const myNewURL = refineURL();
      window.history.pushState({}, "DAR", "/" + myNewURL);
      sessionStorage.setItem("clientToken", clientToken);
    } catch (e) {
      console.log(
        "sessionStorage for token or history not available, probably running in iframe"
      );
    }
  }
  let data: InitialResponse;
  try {
    data = await getInitialData(clientToken);
  } catch (e) {
    store.commit("setConnected", false);
    console.error("UNABLE TO CONNECT TO SERVER", e);
    setTimeout(() => createConnection(store, false), 3000);
    return;
  }
  initHeap({
    environmentId: process.env.VUE_APP_HEAP_ENVIRONMENT_ID,
    userId: data?.user?.heapAdminUniqueId,
    userProperties: {
      tenant: data.tenantId,
      "partner-ticker": data.partnerTicker,
      "admin-name": data?.user?.fullName,
      "admin-email": data?.user?.email
    },
    config: {
      disableTextCapture: true
    }
  });

  connection = new WebSocket(WS_ENDPOINT);

  interval = setInterval(() => {
    sendWebsocketMessage({ type: "ping" });
    store.commit("setPingTime", new Date().getTime());
  }, 3000);

  await store.dispatch("initialSetup", data);
  await store.commit("setToken", clientToken);
  const authMessage: AuthMessage = {
    id: "",
    type: "authMe",
    token: store.getters.clientToken
  };

  connection.onopen = () => {
    sendWebsocketMessage(authMessage);
    store.commit("setConnected", true);
    store.commit("setInitialLoad", false);
  };

  connection.onclose = event => {
    clearInterval(interval);
    console.log("connection died, reconnecting..", event);
    if (!isFirstConnection) {
      store.commit("setConnected", false);
      setTimeout(() => createConnection(store, false), 3000);
    } else {
      const con = createConnection(store, false);
      setTimeout(async () => {
        if ((await con)?.readyState !== WebSocket.OPEN)
          store.commit("setConnected", false);
      }, 1000);
    }
  };

  connection.onerror = error => {
    console.error("WS error", error);
    store.commit("setConnected", false);
    setTimeout(() => createConnection(store), 3000);
  };

  connection.onmessage = event => {
    const msg: OutcomeWebsocketMessage = JSON.parse(event.data);

    // Sanity check
    // if (msg.previousId) {
    // if (msg.previousId !== lastServerMessageId && !!lastServerMessageId) {
    // connection.close(3888, "Inconsistent data.");
    // }
    // lastServerMessageId = msg.id;
    // }

    handleIncomingMessages(msg, store);
  };

  return connection;
};
